/**
 * lib/help-links.mjs — the links a help-chat answer may carry.
 *
 * WHY (2026-09-15, Vela — Lars: "every link must be clickable and never
 * broken"). Answers wrote pages as plain text ("pianofantasy.com/contact") and
 * the chat bubble rendered text only, so nothing was clickable; and nothing
 * stopped an answer naming a page that does not exist.
 *
 * The rule, applied to every answer on the server (sanitizeAnswerLinks):
 *   - A link may point only at a URL on the verified list: the pianofantasy.com
 *     pages in the search index (the help library the answer was grounded in),
 *     the pages the instructions' URL map names, and the app.pianofantasy.com
 *     routes in APP_ROUTES.
 *   - An allowed link comes back as a markdown link `[label](href)`, which the
 *     chat widget renders as a real <a>. A plain-text allowed URL is turned
 *     into one.
 *   - Anything else is removed: a markdown link keeps its label, a raw URL is
 *     dropped, so no raw URL is ever shown.
 *   - A URL the daily link check found broken (the `dead` set) counts as not
 *     on the list until a later check finds it healthy again.
 */

export const SITE_ORIGIN = 'https://pianofantasy.com';
export const APP_ORIGIN = 'https://app.pianofantasy.com';

/**
 * Site pages named in the chat instructions' "Key product and page URL map"
 * (app/api/help-search/route.ts). They are real pages but not help-library
 * documents, so the index alone would not list them.
 */
export const SITE_PAGES = Object.freeze(['/', '/help', '/pro-member', '/super-fingers', '/store', '/contact', '/blog']);

/**
 * app.pianofantasy.com routes a visitor or member can be sent to. Static routes
 * only (no [slug] segments), taken from pf-platform's page routes on origin/main
 * f21dabe0 (2026-09-15). Signed-out visitors are redirected to sign-in, which
 * the daily check follows and accepts as healthy.
 */
export const APP_ROUTES = Object.freeze([
  '/',
  '/sign-in',
  '/forgot-password',
  '/courses',
  '/events',
  '/community',
  '/resources',
  '/journey',
  '/settings/account',
  '/settings/billing',
  '/settings/notifications',
  '/settings/profile',
]);

/** Strip query, fragment and trailing slash; lower-case. '/' stays '/'. */
function normalizePath(path) {
  let p = String(path || '/').split('#')[0].split('?')[0].toLowerCase();
  if (!p.startsWith('/')) p = '/' + p;
  if (p.length > 1) p = p.replace(/\/+$/, '');
  return p || '/';
}

/**
 * The verified list as canonical absolute URLs without fragment
 * ("https://pianofantasy.com/contact"). `index` is the help search index
 * (docs[i][2] is the root-relative sourceUrl).
 * @param {{ docs: Array<[string, string, string, number]> } | null} index
 * @returns {string[]}
 */
export function buildLinkAllowlist(index) {
  const out = new Set();
  for (const p of SITE_PAGES) out.add(SITE_ORIGIN + normalizePath(p));
  for (const d of index?.docs ?? []) {
    if (typeof d?.[2] === 'string' && d[2].startsWith('/')) out.add(SITE_ORIGIN + normalizePath(d[2]));
  }
  for (const p of APP_ROUTES) out.add(APP_ORIGIN + normalizePath(p));
  return [...out].sort();
}

/**
 * Resolve a link target as the model wrote it to { key, href }, or null when it
 * is not a pianofantasy.com / app.pianofantasy.com URL at all.
 *   key  — canonical allowlist key (no fragment)
 *   href — what the widget links to: root-relative for the site (so staging
 *          links stay on staging), absolute for the app; fragment kept.
 * @param {string} raw
 */
export function resolveTarget(raw) {
  const t = String(raw || '').trim();
  if (!t) return null;
  if (t.startsWith('/') && !t.startsWith('//')) {
    const frag = t.includes('#') ? '#' + t.split('#')[1] : '';
    const path = normalizePath(t);
    return { key: SITE_ORIGIN + path, href: path + frag };
  }
  const m = /^(?:https?:\/\/)?(www\.|app\.|staging\.|staging-app\.)?pianofantasy\.com(\/[^\s]*)?$/i.exec(t);
  if (!m) return null;
  const sub = (m[1] || '').toLowerCase();
  const rest = m[2] || '/';
  const frag = rest.includes('#') ? '#' + rest.split('#')[1] : '';
  const path = normalizePath(rest);
  if (sub === 'app.' || sub === 'staging-app.') {
    return { key: APP_ORIGIN + path, href: APP_ORIGIN + path + frag };
  }
  return { key: SITE_ORIGIN + path, href: path + frag };
}

const TRAILING_PUNCT = /[.,;:!?'"’”)\]]+$/;

/**
 * Rewrite an answer's links against the verified list. Returns the new text and
 * a count of what happened, for the log line.
 * @param {string} text
 * @param {Set<string> | string[]} allowlist canonical keys (buildLinkAllowlist)
 * @param {Set<string> | string[]} [dead] canonical keys the daily check found broken
 */
export function sanitizeAnswerLinks(text, allowlist, dead = []) {
  const allow = allowlist instanceof Set ? allowlist : new Set(allowlist);
  const broken = dead instanceof Set ? dead : new Set(dead);
  const stats = { kept: 0, linked: 0, removed: 0 };
  if (typeof text !== 'string' || text === '') return { text: text ?? '', stats };

  const ok = (target) => {
    const r = resolveTarget(target);
    return r && allow.has(r.key) && !broken.has(r.key) ? r : null;
  };
  const looksLikeUrl = (s) => /^(?:https?:\/\/|www\.)\S+$|^\S*pianofantasy\.com(\/\S*)?$/i.test(s.trim());

  // Markdown links are cut out first so the bare-URL pass never sees their parts.
  const placeholders = [];
  const hold = (s) => `\u0000${placeholders.push(s) - 1}\u0000`;
  let out = text.replace(/\[([^\]\n]{1,200})\]\(([^()\s]{1,500})\)/g, (_all, label, target) => {
    const r = ok(target);
    if (r) {
      stats.kept++;
      return hold(`[${label}](${r.href})`);
    }
    stats.removed++;
    return looksLikeUrl(label) ? '' : label;
  });

  // Bare URLs: any http(s) URL, and pianofantasy.com hosts written without a scheme.
  // Not preceded by a word character, "@" (e-mail addresses) or "/" or ".".
  out = out.replace(
    /(?<![\w@/.\u0000])((?:https?:\/\/)\S+|(?:(?:www|app|staging|staging-app)\.)?pianofantasy\.com(?:\/[^\s]*)?)/gi,
    (all) => {
      const trail = (all.match(TRAILING_PUNCT) || [''])[0];
      const url = trail ? all.slice(0, -trail.length) : all;
      const r = ok(url);
      if (r) {
        stats.linked++;
        return hold(`[${url}](${r.href})`) + trail;
      }
      stats.removed++;
      return trail;
    },
  );

  // Bare root-relative paths written as prose (" /contact").
  out = out.replace(/(^|[\s(])(\/[a-z0-9][a-z0-9-]*(?:\/[a-z0-9][a-z0-9-]*)*\/?(?:#[a-z0-9-]+)?)(?=$|[\s.,;:!?)])/gim, (all, pre, path) => {
    const r = ok(path);
    if (r) {
      stats.linked++;
      return pre + hold(`[${path}](${r.href})`);
    }
    stats.removed++;
    return pre;
  });

  out = out.replace(/\u0000(\d+)\u0000/g, (_m, i) => placeholders[Number(i)]);
  // Tidy what a removal can leave behind: doubled spaces, a space before punctuation, empty brackets.
  out = out.replace(/\(\s*\)/g, '').replace(/[ \t]{2,}/g, ' ').replace(/ +([.,;:!?])/g, '$1');
  return { text: out, stats };
}

/**
 * Split an answer into text and link segments for rendering. Only links whose
 * href is root-relative or on pianofantasy.com / app.pianofantasy.com become
 * link segments; the server already removed everything else.
 * @param {string} text
 * @returns {Array<{ type: 'text', text: string } | { type: 'link', text: string, href: string }>}
 */
export function answerSegments(text) {
  const segs = [];
  const re = /\[([^\]\n]{1,200})\]\(((?:\/(?!\/)|https:\/\/(?:app\.)?pianofantasy\.com\/?)[^()\s]{0,500})\)/g;
  let last = 0;
  let m;
  const s = String(text ?? '');
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) segs.push({ type: 'text', text: s.slice(last, m.index) });
    segs.push({ type: 'link', text: m[1], href: m[2] });
    last = m.index + m[0].length;
  }
  if (last < s.length) segs.push({ type: 'text', text: s.slice(last) });
  return segs;
}
